import { ReactElement } from 'react';

import { Paginator } from './globalModels';

/* Table */
export enum TableOperator {
  STRING = 'STRING',
  BOOLEAN = 'BOOLEAN',
  CHOICE = 'CHOICE',
  DATE = 'DATE',
  DECIMAL = 'DECIMAL',
  EMAIL = 'EMAIL',
  INTEGER = 'INTEGER',
  LIST = 'LIST',
  MONEY = 'MONEY',
  TYPEAHEAD = 'TYPEAHEAD',
  URL = 'URL',
}

export enum ColumnWidth {
  tiny,
  small,
  medium,
  large,
}

export enum TableIdEnum {
  ACTIVITY = 'activity',
  ASSETS = 'assets',
  SHARES = 'shares',
  MONETARY_REWARDS = 'monetary_rewards',
  REDEMPTION_TRANSACTION = 'redemption_transaction',
  REDEMPTION_ITEMS = 'redemption_items',
  REFERRAL_NETWORK = 'referral_network',
}

export enum TableColumnValueType {
  BOOLEAN = 'BOOLEAN',
  DATE = 'DATE',
  STRING = 'STRING',
  CHOICE = 'CHOICE',
  TYPEAHEAD = 'TYPEAHEAD',
  EMAIL = 'EMAIL',
  DECIMAL = 'DECIMAL',
  INTEGER = 'INTEGER',
}

export type FilterConditionGroupItem = {
  field: string;
  value?: any;
  modifier?: string;
  condition?: string;
  negated?: boolean;
  operator?: string;
  default?: boolean;
  required?: boolean;
};

export type FilterCondition = {
  condition?: string;
  filter_group: FilterConditionGroupItem[];
  type?: string;
};

export type FilterObj = {
  display_name?: string; //"Unsubscribed",
  created_at?: string;
  slug?: string;
  url?: string;
  page?: number;
  page_size?: number;
  id?: string;
  filters?: FilterCondition[];
  columns?: string[];
  company_user_uid?: string;
  row_type?: string;
  ordering?: string | string[];
};

export type TableDefaultColumn = {
  display_name: string;
  value_type: string;
  width: ColumnWidth | number | string;
  is_required: boolean;
  field: string;
  default_sort_direction?: undefined;
  custom?: undefined;
  hidden?: boolean;
  disable_sorting?: undefined;
  prefetch_related_model?: undefined;
};

export type TableDefault = {
  id: null;
  url: null;
  company_user_uid: null;
  created_at: null;
  display_name: string;
  filters: never[];
  columns: TableDefaultColumn[];
};

export type TableModifiers = {
  // [...TableOperator]: any
};

export type TypeHeadOptionConfig = {
  display_field: string;
  display_url: string;
  field: string;
  url: string;
};

export type TableOptionFilter = {
  choices?: string[][];
  display_name: string;
  key: string;
  negated_operators?: string[] | null;
  hidden?: boolean;
  operators?: string[];
  value_type: TableColumnValueType;
  typeahead?: TypeHeadOptionConfig;
  custom?: boolean;
};

export type TableOptionFilters = {
  [key: string]: TableOptionFilter;
};

export type ImportColumnType = {
  display_name: string;
  field: string;
  is_required?: boolean;
  value_type: TableOperator;
};

export type TableOptionFiltersOperators = {
  [key: string]: any; // To irregular to type
};

export type TableOptions = {
  columns: TableDefaultColumn[];
  operators: TableOptionFiltersOperators;
  negated_operators: TableOptionFiltersOperators | null;
  filters: TableOptionFilters;
  default_tables: TableDefault[];
  import_columns: ImportColumnType[] | null;
  modifiers: TableModifiers;
  bulk_delete_columns?: ImportColumnType[] | null;
};

// TODO: REMOVE FROM HERE
export type FinalTableProps = {
  rowStyle: {
    minWidth: number;
  };
  gridStyle: {
    minWidth: number;
  };
  width: number;
  sortDirection: string;
  sort: Function;
  sortBy: string;
  rowCount: number;
  headerHeight: number;
  rowHeight: number;
  height: number;
  ref?: HTMLDivElement;
  onRowsRendered?: Function;
  rowGetter: Function;
  overscanRowCount: number;
  rowClassName: Function;
};
export type TableColumnProps = {
  key: number;
  dataKey: string;
  label: string;
  width: number;
  flexGrow: number;
  flexShrink: number;
  defaultSortDirection: undefined;
  disableSort: undefined;
  headerRenderer: (cell: CellData) => any;
  cellRenderer: (cell: CellData) => any;
  headerClassName: any;
};

export type TableProps = {
  rowClassName: Function;
  tableActions?: any;
  actionRow?: ReactElement;
  loading?: boolean;
  overscanRowCount: number;
  totalCount: number;
  selectedCount: number;
  noResultsText: string;
  noFilteredResultsText: string;
  noSearchResultsText: string;
  isRowLoaded?: (params: CellData) => boolean;
  loadMoreRows?: (params: CellData) => any;
  headerHeight: number;
  rowHeight: number;
  rowCount: number;
  displayRows?: number;
  sort: Function;
  sortBy: string;
  sortDirection: string;
  autoHeightAdjust: boolean;
  inTableSet: boolean;
  staticHeight?: number;
  noResultsNode?: React.ReactElement;
  isSearched: boolean;
  isFiltered: boolean;
  columns: TableColumnProps[];
  onSelectRow?: Function;
  onDeselectRow?: Function;
  onSelectAllRows?: Function;
  onDeselectAllRows?: Function;
  rowGetter: Function;
};
export type SortObject = {
  sortDirection: string;
  sortBy: string;
};
export type ColumnData<T> = {
  cellData: CellData[];
  columnData: null;
  columnIndex: number;
  dataKey: string;
  isScrolling: boolean;
  parent: JSX.Element;
  rowData: T;
  rowIndex: number;
};

export type CellData = {
  name: string;
  isSelected: boolean;
  rowIndex: number;
  index: number;
  cellData: any;
  rowData: any;
  columnData: any;
  dataKey?: string;
  disableSort: boolean;
  label: string;
  sortBy: string;
  sortDirection: string;
};
export type TextLinkProps = {
  onClick: Function;
  displayText: string;
  tooltip: string;
};
export type StatusBadgeProps = {
  text: string;
  color: string;
};
export type ConfiguredCellType = {
  cell: CellData;
  title?: string;
  type: string;
  cellRenderer: Function;
};
export type DateCellType = {
  cell: CellData;
  format?: string;
};
export type ConfiguredHeaderCellType = {
  headerRenderer?: Function;
  cell: any;
};
export type HeaderCellType = {
  columnData: any;
  dataKey: string;
  disableSort: boolean;
  extraClassName?: string;
  label: string;
  sortBy: string;
  sortDirection: string;
};
export type CheckboxCellType = {
  handleSelect: Function;
  cell: CellData;
};
export type SelectRowsCellType = {
  handleSelect: Function;
  totalCount: number;
  selectedRows: number;
  column: any;
};

export type PageNavigatorType<T> = {
  data: Paginator<T>;
  separated: number;
  onClick: Function;
};
